import './header-inline-nav.js';

const introSeenKey = 'bardem:cover-intro-seen';
const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
let returningVisit = false;
try { returningVisit = sessionStorage.getItem(introSeenKey) === '1'; } catch { /* Storage may be disabled. */ }

// Keep the first visit's ordering stable through its feature load, but remember
// the completed intro for reloads and later page visits in this tab/session.
const rememberIntro = () => {
  if (window.Shopify?.designMode) return;
  try { sessionStorage.setItem(introSeenKey, '1'); } catch { /* Loading must still work without storage. */ }
};
document.addEventListener('hero:intro-settled', rememberIntro);
if (document.querySelector('product-overlay-hero:not([data-cover-loading]):not([data-intro-pending]):not([data-building])')) rememberIntro();

export function shouldSequenceSections() {
  if (document.documentElement.hasAttribute('data-localization-arrival')) return false;
  const constrained = connection?.saveData || ['slow-2g', '2g', '3g'].includes(connection?.effectiveType);
  return !returningVisit && !constrained && !window.Shopify?.designMode;
}

export function isHeroBlocking() {
  return shouldSequenceSections() && !!document.querySelector('product-overlay-hero:is([data-cover-loading], [data-intro-pending], [data-building])');
}

// Dependencies follow the visible page, not a timer for every independent widget.
export async function decodeImage(image) {
  image.loading = 'eager';
  if (image.decode) await image.decode();
  else if (!image.complete) await new Promise((resolve, reject) => {
    image.addEventListener('load', resolve, { once: true });
    image.addEventListener('error', reject, { once: true });
  });
  if (!image.naturalWidth) throw new Error('Image unavailable');
}

export function waitForHero() {
  if (!isHeroBlocking()) return Promise.resolve();
  return new Promise(resolve => {
    const finish = () => {
      clearTimeout(timer);
      document.removeEventListener('hero:intro-settled', finish);
      connection?.removeEventListener?.('change', onConnectionChange);
      resolve();
    };
    const onConnectionChange = () => { if (!isHeroBlocking()) finish(); };
    const timer = setTimeout(finish, 6500);
    document.addEventListener('hero:intro-settled', finish, { once: true });
    connection?.addEventListener?.('change', onConnectionChange);
  });
}

class StorefrontFeatures extends HTMLElement {
  ready;
  connectedCallback() {
    if (this.ready) return;
    this.ready = this.#prepare();
  }
  async #prepare() {
    if (!window.Shopify?.designMode) await waitForHero();
    if (!this.isConnected) { this.ready = null; return; }
    for (const template of this.querySelectorAll('template[data-feature-media]')) {
      template.replaceWith(template.content.cloneNode(true));
    }
    const images = [...this.querySelectorAll('.value-proposition__icon')];
    await Promise.race([Promise.allSettled(images.map(decodeImage)), new Promise(resolve => setTimeout(resolve, 3000))]);
    if (this.isConnected) this.setAttribute('data-features-ready', '');
  }
}
if (!customElements.get('storefront-features')) customElements.define('storefront-features', StorefrontFeatures);

export async function waitForFeatures() {
  // Return visits and constrained connections prepare each section independently.
  // Actual image readiness still controls its placeholder, not the cover intro.
  if (!shouldSequenceSections()) return;
  await waitForHero();
  if (!shouldSequenceSections()) return;
  await Promise.all([...document.querySelectorAll('storefront-features')].map(section => section.ready));
}
